// Breadcrumb and Application State: the parts that frame a page.
import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";

/**
 * <i80-breadcrumb>
 *   <i80-crumb href="/plans" icon="folder">Plans</i80-crumb>
 *   <i80-crumb current>Q3 launch</i80-crumb>
 * </i80-breadcrumb>
 *
 * Real links in an ordered list. The last item, or one marked current, is text with
 * aria-current="page" instead of a link.
 */
export class I80Breadcrumb extends I80Element {
  static get observedAttributes() {
    return ["aria-label", "items-can-wrap"];
  }

  setup() {
    let nav = this.part("breadcrumb");
    if (!nav) {
      nav = h("nav", { "data-i80-part": "breadcrumb" }, h("ol", { class: "i80-breadcrumb__list", "data-i80-part": "list" }, this.takeContent()));
      this.append(nav);
    }
    this._nav = nav;
  }

  adopt() {
    this._nav.querySelector('[data-i80-part="list"]').append(...this.takeContent());
  }

  update() {
    this._nav.className = ["i80-breadcrumb", this.flag("items-can-wrap") && "i80-breadcrumb--items-can-wrap"].filter(Boolean).join(" ");
    this._nav.setAttribute("aria-label", this.getAttribute("aria-label") ?? "Breadcrumb");
    // <ol> may only contain <li>, so each crumb is wrapped once.
    const list = this._nav.querySelector('[data-i80-part="list"]');
    for (const crumb of [...list.querySelectorAll("i80-crumb")]) {
      if (!crumb.parentElement?.matches("li")) {
        const item = h("li", { class: "i80-breadcrumb__item" });
        crumb.before(item);
        item.append(crumb);
      }
      crumb.parentElement.className = "i80-breadcrumb__item";
      // A crumb may not be upgraded yet (it is defined after this element).
      crumb.update?.();
    }
  }
}

/** One step in a breadcrumb. Give it an href, or `current` for the page you are on. */
export class I80Crumb extends I80Element {
  static get observedAttributes() {
    return ["href", "icon", "current", "external"];
  }

  setup() {
    this._content = this.part("content") ?? h("span", { "data-i80-part": "content" }, this.takeContent());
    if (!this._content.isConnected) this.append(this._content);
  }

  adopt() {
    const text = this.querySelector(".i80-breadcrumb__text") ?? this.part("content");
    for (const node of this.takeContent()) text.append(node);
  }

  update() {
    // The last crumb is the page you are on, unless it links somewhere.
    const isLast = !(this.closest("li") ?? this).nextElementSibling;
    const current = this.flag("current") || (isLast && !this.hasAttribute("href"));
    const href = this.getAttribute("href");
    const name = this.getAttribute("icon");
    const text = this._content.querySelector(".i80-breadcrumb__text") ?? h("span", { class: "i80-breadcrumb__text" }, ...this._content.childNodes);
    text.className = "i80-breadcrumb__text";

    // The wrapper changes between <a> and <div>, so it is rebuilt around the same text node.
    const wrapper = current || !href
      ? h("div", { class: "i80-breadcrumb__current", "data-i80-part": "content", "aria-current": current ? "page" : null })
      : h("a", { class: "i80-breadcrumb__link", "data-i80-part": "content", href, target: this.flag("external") ? "_blank" : null, rel: this.flag("external") ? "noopener noreferrer" : null });
    if (name) wrapper.append(h("div", { class: "i80-breadcrumb__icon" }, icon(name, { stretched: true })));
    wrapper.append(text);
    this._content.replaceWith(wrapper);
    this._content = wrapper;
  }
}

/**
 * <i80-application-state title="No plans yet" icon="folder" align="center">
 *   <p slot="body">Plans keep tasks, dates and owners in one place.</p>
 *   <i80-button slot="footer" color="primary">New plan</i80-button>
 * </i80-application-state>
 *
 * The empty, error and loading screens: an optional error code, a title, body text and
 * actions. Anything with slot="media" goes above the title.
 */
export class I80ApplicationState extends I80Element {
  static get observedAttributes() {
    return ["title", "icon", "error-code", "align", "auto-centered", "title-tag"];
  }

  setup() {
    let root = this.part("state");
    if (!root) {
      const slot = (name) => [...this.children].filter((c) => c.getAttribute("slot") === name);
      const media = slot("media");
      const footer = slot("footer");
      const body = this.takeContent([...media, ...footer]);
      root = h("div", { "data-i80-part": "state" },
        media.length ? h("div", { class: "i80-application-state__media" }, media) : null,
        h("div", { class: "i80-application-state__header", "data-i80-part": "header" }),
        body.length ? h("div", { class: "i80-application-state__body" }, body) : null,
        footer.length ? h("div", { class: "i80-application-state__footer" }, footer) : null);
      this.append(root);
    }
    this._root = root;
  }

  adopt() {
    const root = this._root;
    const box = (name, cls) =>
      root.querySelector(`.i80-application-state__${name}`) ??
      root.appendChild(h("div", { class: cls }));
    for (const node of this.takeContent()) {
      const slot = node.nodeType === 1 ? node.getAttribute("slot") : null;
      if (slot === "footer") box("footer", "i80-application-state__footer").append(node);
      else if (slot === "media") root.prepend(h("div", { class: "i80-application-state__media" }, node));
      else box("body", "i80-application-state__body").append(node);
    }
    // The header must stay above the body even when the body was added later.
    const header = root.querySelector('[data-i80-part="header"]');
    const body = root.querySelector(".i80-application-state__body");
    if (header && body && header.compareDocumentPosition(body) & Node.DOCUMENT_POSITION_PRECEDING) header.before(body);
  }

  update() {
    this._root.className = [
      "i80-application-state",
      `i80-application-state--align-${this.choice("align", ["left", "center"], "left")}`,
      this.flag("auto-centered") && "i80-application-state--is-auto-centered",
    ].filter(Boolean).join(" ");

    const header = this._root.querySelector('[data-i80-part="header"]');
    const code = this.getAttribute("error-code");
    const name = this.getAttribute("icon");
    const tag = this.choice("title-tag", ["div", "h1", "h2", "h3", "h4", "h5", "h6"], "div");
    header.replaceChildren(...[
      code ? h("div", { class: "i80-application-state__error-code i80-text i80-typography-body-100 i80-font-weight-medium i80-foreground-faint" }, `ERROR ${code}`) : null,
      name ? h("div", { class: "i80-application-state__icon" }, icon(name, { size: "24" })) : null,
      h(tag, { class: "i80-application-state__title i80-text i80-typography-display-300 i80-font-weight-semibold i80-foreground-strong" }, this.getAttribute("title") ?? ""),
    ].filter(Boolean));

    for (const el of this._root.querySelectorAll(".i80-application-state__body > p:not([class])")) {
      el.className = "i80-application-state__body-text i80-text i80-typography-body-300 i80-font-weight-regular";
    }
    for (const b of this._root.querySelectorAll('.i80-application-state__footer > i80-button:not([size]), .i80-application-state__footer > i80-link-standalone:not([size])')) {
      b.setAttribute("size", "medium");
    }
  }
}

define("i80-breadcrumb", I80Breadcrumb);
define("i80-crumb", I80Crumb);
define("i80-application-state", I80ApplicationState);
